import { QuestionMarkCircleIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { ReactNode, useEffect, useState } from 'react';

const STORAGE_KEY = 'hasSeenView3DHelp';

function MouseLeftClickIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
    >
      <path
        d='M6 10V17C6 19.7614 8.6863 22 12 22C15.3137 22 18 19.7614 18 17V10'
        strokeLinecap='round'
      />
      <path
        d='M6 10V7C6 4.2386 8.6863 2 12 2C15.3137 2 18 4.2386 18 7V10'
        strokeLinecap='round'
      />
      <path d='M12 2V10M6 10H18' strokeLinecap='round' />
      <path
        d='M6 7C6 4.2386 8.6863 2 12 2V10H6V7Z'
        fill='currentColor'
        stroke='none'
      />
    </svg>
  );
}

function MouseRightClickIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
    >
      <path
        d='M6 10V17C6 19.7614 8.6863 22 12 22C15.3137 22 18 19.7614 18 17V10'
        strokeLinecap='round'
      />
      <path
        d='M6 10V7C6 4.2386 8.6863 2 12 2C15.3137 2 18 4.2386 18 7V10'
        strokeLinecap='round'
      />
      <path d='M12 2V10M6 10H18' strokeLinecap='round' />
      <path
        d='M12 2C15.3137 2 18 4.2386 18 7V10H12V2Z'
        fill='currentColor'
        stroke='none'
      />
    </svg>
  );
}

function MouseScrollIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
    >
      <path
        d='M6 10V17C6 19.7614 8.6863 22 12 22C15.3137 22 18 19.7614 18 17V10'
        strokeLinecap='round'
      />
      <path
        d='M6 10V7C6 4.2386 8.6863 2 12 2C15.3137 2 18 4.2386 18 7V10'
        strokeLinecap='round'
      />
      <path d='M12 2V10M6 10H18' strokeLinecap='round' />
      <rect x='10.5' y='4' width='3' height='5' rx='1.5' fill='currentColor' />
    </svg>
  );
}

type ShortcutItemProps = {
  keys: string[];
  description: ReactNode;
  type?: 'keyboard' | 'left click' | 'right click' | 'scroll';
};

function ShortcutItem({
  keys,
  description,
  type = 'keyboard'
}: ShortcutItemProps) {
  const renderIcon = () => {
    if (type === 'left click') {
      return <MouseLeftClickIcon className='h-6 w-6' />;
    }
    if (type === 'right click') {
      return <MouseRightClickIcon className='h-6 w-6' />;
    }
    if (type === 'scroll') {
      return <MouseScrollIcon className='h-6 w-6' />;
    }
    return null;
  };

  return (
    <div className='flex items-start gap-3'>
      <div className='flex w-24 shrink-0 items-center gap-1'>
        {keys.map((key, index) => (
          <span key={key}>
            {type === 'keyboard' ? (
              <kbd className='inline-flex min-w-[2rem] items-center justify-center rounded border border-gray-300 bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-700 shadow-sm'>
                {key}
              </kbd>
            ) : (
              <span className='inline-flex items-center gap-1 px-1 py-1 text-xs font-medium text-gray-600'>
                {renderIcon()}
                {key}
              </span>
            )}
            {index < keys.length - 1 && (
              <span className='mx-1 text-gray-400'>+</span>
            )}
          </span>
        ))}
      </div>
      <span className='text-sm text-gray-600'>{description}</span>
    </div>
  );
}

export default function View3DHelpPopup() {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const hasSeen = localStorage.getItem(STORAGE_KEY);
    if (!hasSeen) {
      setIsOpen(true);
    }
  }, []);

  const handleDismiss = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setIsOpen(false);
  };

  const handleOpen = () => {
    setIsOpen(true);
  };

  if (!isOpen) {
    return (
      <button
        type='button'
        onClick={handleOpen}
        className='absolute bottom-2 left-2 z-50 rounded-full border border-gray-200 bg-white p-2 text-gray-500 shadow-lg hover:bg-gray-50 hover:text-gray-700'
        aria-label='Show 3D view controls'
      >
        <QuestionMarkCircleIcon className='h-5 w-5' />
      </button>
    );
  }

  return (
    <div className='absolute bottom-2 left-2 z-50 w-72 animate-fade-in rounded-lg border border-gray-200 bg-white p-4 shadow-lg'>
      <div className='mb-3 flex items-center justify-between'>
        <h3 className='text-sm font-semibold text-gray-900'>
          3D View Controls
        </h3>
        <button
          type='button'
          onClick={handleDismiss}
          className='rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600'
          aria-label='Dismiss 3D view controls popup'
        >
          <XMarkIcon className='h-4 w-4' />
        </button>
      </div>
      <div className='flex flex-col gap-2.5'>
        <ShortcutItem
          keys={['Left Button']}
          description={
            <>
              <span className='font-semibold'>Click + Drag:</span> Rotate view
            </>
          }
          type='left click'
        />
        <ShortcutItem
          keys={['Right Button']}
          description={
            <>
              <span className='font-semibold'>Click + Drag:</span> Pan view
            </>
          }
          type='right click'
        />
        <ShortcutItem
          keys={['Mouse Wheel']}
          description={
            <>
              <span className='font-semibold'>Scroll:</span> Zoom in / zoom out
            </>
          }
          type='scroll'
        />
        <ShortcutItem
          keys={['Left Button']}
          description={
            <>
              <span className='font-semibold'>
                Click line set point, duct point, or equipment:
              </span>{' '}
              Show move arrows to adjust position
            </>
          }
          type='left click'
        />
        <ShortcutItem
          keys={['Left Button']}
          description={
            <>
              <span className='font-semibold'>
                Drag line set or duct point:
              </span>{' '}
              Move it directly along the plane facing the camera
            </>
          }
          type='left click'
        />
        <ShortcutItem
          keys={['Right Button']}
          description={
            <>
              <span className='font-semibold'>
                Click equipment, line set, or duct run:
              </span>{' '}
              Open options menu
            </>
          }
          type='right click'
        />
      </div>
    </div>
  );
}
